import { api, SubscriptionDetail, User } from "../api";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export type AnalyticsPlanInterval = "year" | "month";

/** What is being bought: the plan and what is charged now (in the currency's unit, e.g. 119.9). */
export interface AnalyticsPurchase {
  interval: AnalyticsPlanInterval;
  value: number;
  currency?: string;
  /** Stripe invoice / payment intent id: the same one the API sends server-side, so it's counted once. */
  transactionId?: string | null;
  paymentType?: string;
  coupon?: string;
}

/**
 * Events pushed to Google Tag Manager's dataLayer (GTM forwards them to Google Analytics / Google Ads).
 *
 * - `sign_up`: once per account (the API says `new` only the first time the account is loaded);
 * - `login`: once per browser session;
 * - `onboarding_complete`, `site_publish` (with `first_publish`), `subscription_cancel`;
 * - `pre_checkout` (plans page), `checkout` + `begin_checkout` (checkout page);
 * - `purchase`: with GA4's `ecommerce` object (value, currency, transaction_id, items);
 * - `purchase_pending`: a boleto generated and not paid yet.
 *
 * The purchase that counts for revenue is the one the API sends server-side when Stripe confirms the
 * payment (it also covers Pix/boleto paid later and renewals); the one here carries the same
 * `transaction_id` when it's already known, so Analytics doesn't count the sale twice.
 */
class KeepoInternalAnalyticsService {
  /** `params` go with the `sign_up` only (e.g. the ad page's niche). */
  notifyLoginEvent(user: User, params?: Record<string, unknown>) {
    if (user.new) {
      this.publishOnce(localStorage, `Keepo.analytics.signUp.${user.id}`, "sign_up", user, params);
    }

    this.publishOnce(sessionStorage, `Keepo.analytics.login.${user.id}`, "login", user);
  }

  notifyOnboardingCompletedEvent(user: User) {
    this.publishOnce(localStorage, `Keepo.analytics.onboarding.${user.id}`, "onboarding_complete", user);
  }

  notifySitePublishedEvent(user: User, siteId: string, firstPublish: boolean) {
    this.publishEvent("site_publish", user, { site_id: siteId, first_publish: firstPublish });
  }

  notifySubscriptionCanceledEvent(user: User, siteId: string) {
    this.publishEvent("subscription_cancel", user, { site_id: siteId });
  }

  notifyPreCheckoutEvent(user: User) {
    this.publishEvent("pre_checkout", user);
  }

  notifyCheckoutEvent(user: User, purchase?: AnalyticsPurchase) {
    this.publishEvent("checkout", user);

    if (purchase) {
      this.publishEcommerceEvent("begin_checkout", user, purchase);
    }
  }

  /**
   * Without the payment's id there's no telling this sale from the one the API reports server-side (the
   * webhook may not have arrived yet): it goes with no `ecommerce`, so revenue isn't counted twice.
   */
  notifyPurchaseEvent(user: User, purchase?: AnalyticsPurchase) {
    if (purchase?.transactionId) {
      this.publishEcommerceEvent("purchase", user, purchase);
    } else {
      this.publishEvent("purchase", user);
    }
  }

  /** A boleto was generated but isn't paid yet: not a purchase until it clears (the API reports that one). */
  notifyPurchasePendingEvent(user: User, purchase?: AnalyticsPurchase) {
    if (purchase) {
      this.publishEcommerceEvent("purchase_pending", user, purchase);
    } else {
      this.publishEvent("purchase_pending", user);
    }
  }

  private publishEcommerceEvent(event: string, user: User, purchase: AnalyticsPurchase) {
    const value = Math.round(purchase.value * 100) / 100;
    const currency = (purchase.currency || "BRL").toUpperCase();

    this.publishEvent(event, user, {
      plan_interval: purchase.interval,
      ...(purchase.paymentType ? { payment_type: purchase.paymentType } : {}),
      ecommerce: {
        ...(purchase.transactionId ? { transaction_id: purchase.transactionId } : {}),
        value,
        currency,
        ...(purchase.coupon ? { coupon: purchase.coupon } : {}),
        items: [
          {
            item_id: `keepo_plus_${purchase.interval}`,
            item_name: purchase.interval === "month" ? "Keepo Plus (mensal)" : "Keepo Plus (anual)",
            item_category: "subscription",
            price: value,
            quantity: 1,
          },
        ],
      },
    });
  }

  private publishEvent(event: string, user: User, params: Record<string, unknown> = {}): boolean {
    try {
      window.dataLayer = window.dataLayer || [];

      // GA4 keeps the last `ecommerce` object in the dataLayer: it's cleared so it doesn't leak into another event
      if ("ecommerce" in params) {
        window.dataLayer.push({ ecommerce: null });
      }

      window.dataLayer.push({
        event,
        method: user.identities?.[0]?.providerType,
        ref: user.ref,
        userId: user.identities?.[0]?.userId,
        user_id: user.id,
        ...params,
      });
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  /**
   * Publishes the event unless `key` is already in the storage; marks it only once published, so an
   * event that couldn't go out is tried again. Without storage (private mode), the event is sent.
   */
  private publishOnce(storage: Storage, key: string, event: string, user: User, params?: Record<string, unknown>) {
    try {
      if (storage.getItem(key)) {
        return;
      }
    } catch (e) {
      // Storage unavailable
    }

    if (!this.publishEvent(event, user, params)) {
      return;
    }

    try {
      storage.setItem(key, "1");
    } catch (e) {
      // Storage unavailable
    }
  }
}

export const keepoInternalAnalyticsService = new KeepoInternalAnalyticsService();

/** How long a recorded payment is taken as "the one just made" (and not an older one of the same Keepo). */
const RECENT_PURCHASE_MS = 60 * 60 * 1000;

const recentPurchase = (detail?: SubscriptionDetail) => {
  const purchase = detail?.lastPurchase;
  if (!purchase?.id) {
    return undefined;
  }

  const paidAt = Date.parse(purchase.paidAt);
  return Number.isFinite(paidAt) && Math.abs(Date.now() - paidAt) <= RECENT_PURCHASE_MS ? purchase : undefined;
};

/**
 * The purchase of a Keepo's subscription, from its detail: the payment the API just recorded (its id and
 * the amount actually charged, the same the server-side event carries). Without one (a boleto still to be
 * paid, Stripe's confirmation not in yet), the plan's price and no id. No plan: undefined.
 */
export const purchaseFromSubscription = (detail?: SubscriptionDetail): AnalyticsPurchase | undefined => {
  if (!detail?.plan) {
    return undefined;
  }

  const recorded = recentPurchase(detail);

  return {
    interval: detail.plan.interval,
    value: recorded ? recorded.value : (detail.plan.amount ?? 0) / 100,
    currency: recorded ? recorded.currency : detail.plan.currency,
    transactionId: recorded?.id,
    paymentType: detail.paymentType ?? "card",
  };
};

/** How many times, and how far apart, the success page checks for Stripe's confirmation of the payment. */
export const purchaseConfirmation = { attempts: 8, intervalMs: 1500 };

/**
 * The subscription once the payment is known: confirmed (the API recorded it, see `lastPurchase`) or a
 * boleto still to be paid. The success page usually opens before Stripe's webhook, so it's checked a few
 * times. Not confirmed in time: undefined (the sale is reported by the server-side event alone; a browser
 * event without the payment's id would be counted on top of it).
 */
export const waitForPurchase = async (siteId: string): Promise<SubscriptionDetail | undefined> => {
  for (let attempt = 0; attempt < purchaseConfirmation.attempts; attempt++) {
    if (attempt > 0) {
      await new Promise(resolve => setTimeout(resolve, purchaseConfirmation.intervalMs));
    }

    const detail = await api.getSubscriptionDetail(siteId).catch(() => undefined);
    if (detail?.awaitingPayment || recentPurchase(detail)) {
      return detail;
    }
  }

  return undefined;
};
